(function () {
  'use strict';

  var Core = window.SwymWishlistCore;
  if (!Core) return;

  /** Skip refetch if we successfully loaded within this window (saves redundant Swym calls when toggling tabs). */
  var FRESH_CACHE_MS = 5000;
  /** Hard timeout per Swym call — guards against stuck spinner if neither callback fires. */
  var SWYM_CALL_TIMEOUT_MS = 8000;
  /** When false, next open of Saved tab will fetch. Never set true until a load finishes (avoids stuck empty UI). */
  var loaded = false;
  var savedReloadTimer = null;
  /** Invalidates in-flight Swym responses when a newer load started (tab / refresh). */
  var savedListLoadGen = 0;
  /**
   * Module-level cache of last successfully rendered items + product data.
   * Survives `form.Cart` re-renders triggered by `cart:refresh` (which wipes the saved panel DOM).
   * Lets us paint stale-but-correct items immediately on Saved tab open, even when Swym is unreachable
   * (e.g., relay blocked in incognito → status 0 errors).
   */
  var savedItemsCache = { items: null, products: null, ts: 0 };

  function hasFreshCache() {
    return (
      loaded &&
      savedItemsCache.items &&
      savedItemsCache.items.length > 0 &&
      Date.now() - savedItemsCache.ts < FRESH_CACHE_MS
    );
  }

  function paintFromCache(list, loading, empty) {
    if (!list || !savedItemsCache.items || !savedItemsCache.items.length) return false;
    var tab = document.querySelector('[data-cart-tab="saved"]');
    var priorLabel = tab ? tab.textContent : null;
    renderItems(list, savedItemsCache.items, savedItemsCache.products || {});
    // Restore label — renderItems overwrites it with stale cache count,
    // but an optimistic update may already show the correct newer count.
    if (tab && priorLabel) tab.textContent = priorLabel;
    if (loading) loading.style.display = 'none';
    if (empty) empty.style.display = 'none';
    return true;
  }

  function getCartDrawer() {
    return document.querySelector('#sidebar-cart');
  }

  function isSavedPanelVisible() {
    var cart = getCartDrawer();
    if (!cart) return false;
    var panel = cart.querySelector('[data-cart-panel="saved"]');
    if (!panel) return false;
    return window.getComputedStyle(panel).display !== 'none';
  }

  /**
   * Next open of Saved tab refetches. If Saved is visible, debounced reload so Swym has new data
   * (coalesces cart:refresh + swym:wishlist-updated).
   */
  function invalidateSavedCacheAndMaybeReload(delayMs) {
    loaded = false;
    var wait = typeof delayMs === 'number' ? delayMs : 480;
    clearTimeout(savedReloadTimer);
    savedReloadTimer = setTimeout(function () {
      savedReloadTimer = null;
      if (!isSavedPanelVisible()) return;
      onSwymReady(loadSavedItems);
    }, wait);
  }

  function onSwymReady(callback) {
    if (window._swat) {
      callback(window._swat);
      return;
    }
    if (!window.SwymCallbacks) {
      window.SwymCallbacks = [];
    }
    window.SwymCallbacks.push(callback);
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    var dollars = cents / 100;
    return '$' + dollars.toFixed(2).replace(/\.00$/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  function escapeAttr(str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function extractHandle(url) {
    if (!url) return null;
    var match = url.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function formatTitle(title) {
    var parts = title.split(' :: ');
    return parts[parts.length - 1];
  }

  function getOptionName(option) {
    if (typeof option === 'string') return option.toLowerCase();
    if (option && option.name) return option.name.toLowerCase();
    return '';
  }

  function getVariantMeta(product, variant) {
    if (!product || !variant || !product.options) return [];

    var colorLabels = ['color', 'colour', 'couleur', 'colore', 'farbe'];
    var sizeLabels = ['size', 'taille', 'taglia', 'größe'];
    var color = '';
    var size = '';

    for (var i = 0; i < product.options.length; i++) {
      var name = getOptionName(product.options[i]);
      if (!color && colorLabels.indexOf(name) !== -1) {
        color = variant['option' + (i + 1)] || '';
      }
      if (!size && sizeLabels.indexOf(name) !== -1) {
        size = variant['option' + (i + 1)] || '';
      }
    }

    var parts = [];
    if (color) parts.push('Color: ' + color);
    if (size) parts.push('Size: ' + size);
    return parts;
  }

  function fetchProduct(handle) {
    return fetch(Core.productsJsonUrl(handle))
      .then(function (res) {
        if (!res.ok) throw new Error('Failed');
        return res.json();
      })
      .catch(function () {
        return null;
      });
  }

  // ─── Tab switching ───

  /**
   * Mini-cart section re-render replaces only `form.Cart`, not the header with tabs.
   * Sync tab state + panels so «Saved» active class cannot outlive a fresh bag DOM.
   */
  function setCartDrawerTab(cart, target) {
    if (!cart || (target !== 'bag' && target !== 'saved')) return;

    var tabs = cart.querySelectorAll('[data-cart-tab]');
    var bagPanel = cart.querySelector('.Drawer__Main');
    var savedPanel = cart.querySelector('[data-cart-panel="saved"]');
    var cartEmpty = cart.querySelector('.Cart__Empty');
    var cartFooter = cart.querySelector('.Drawer__Footer');
    var pickupNotice = cart.querySelector('.Cart__PickupShippingNotice');
    var checkoutBtn = cart.querySelector('.Cart__Checkout--Submit');
    var viewBagLink = cart.querySelector('.Button--View_Bag');
    if (!savedPanel) return;

    var activeTab = cart.querySelector('[data-cart-tab="' + target + '"]');
    tabs.forEach(function (t) {
      t.classList.toggle('CartDrawer__Tab--active', t === activeTab);
      t.classList.toggle('CartDrawer__Tab--inactive', t !== activeTab);
    });

    var isSaved = target === 'saved';
    var hiddenDisplay = 'none';
    if (bagPanel) bagPanel.style.display = isSaved ? 'none' : '';
    if (cartEmpty) cartEmpty.style.display = isSaved ? 'none' : '';
    if (cartFooter) cartFooter.style.display = isSaved ? 'none' : '';
    if (pickupNotice) pickupNotice.style.display = isSaved ? 'none' : '';
    if (checkoutBtn) checkoutBtn.style.display = isSaved ? 'none' : '';
    if (viewBagLink) viewBagLink.closest('a') && (viewBagLink.closest('a').style.display = isSaved ? 'none' : '');
    savedPanel.style.display = isSaved ? 'flex' : 'none';
    cart.classList.toggle('Drawer--saved-active', isSaved);
  }

  function resetCartDrawerToBagTab() {
    var cart = document.querySelector('#sidebar-cart');
    if (cart) setCartDrawerTab(cart, 'bag');
  }

  function initTabs() {
    document.addEventListener('click', function (e) {
      var tab = e.target.closest('[data-cart-tab]');
      if (!tab) return;

      var target = tab.getAttribute('data-cart-tab');
      var cart = document.querySelector('#sidebar-cart');
      if (!cart) return;

      if (!cart.querySelector('[data-cart-panel="saved"]')) return;

      setCartDrawerTab(cart, target);

      if (target === 'saved') {
        // Always fetch when opening Saved — a prior load may have failed, returned empty v3
        // before legacy fallback, or raced with cart:refresh; `loaded` must not block retries.
        onSwymReady(loadSavedItems);
      }
    });
  }

  /** Drawer close re-reloads cart HTML without `cart:refresh`; keep tabs in sync. */
  function watchCartDrawerCloseForTabReset() {
    var cart = document.querySelector('#sidebar-cart');
    if (!cart) return;
    var observer = new MutationObserver(function (mutations) {
      var ariaChanged = false;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].attributeName === 'aria-hidden') {
          ariaChanged = true;
          break;
        }
      }
      if (ariaChanged && cart.getAttribute('aria-hidden') === 'true') {
        resetCartDrawerToBagTab();
      }
    });
    observer.observe(cart, { attributes: true, attributeFilter: ['aria-hidden'] });
  }

  // ─── Save for Later (from Bag tab) ───

  function initSaveForLater() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.CartItem__SaveForLater');
      if (!btn) return;

      var productId = parseInt(btn.getAttribute('data-product-id'), 10);
      var variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
      var productUrl = btn.getAttribute('data-product-url');

      // Optimistic: hide the cart item immediately
      var cartItemWrapper = btn.closest('.CartItemWrapper') || btn.closest('.CartItem');
      if (cartItemWrapper) cartItemWrapper.style.display = 'none';

      // Optimistically bump saved count — read from tab label (always up-to-date)
      var currentSavedCount = getSavedTabCount();
      updateSavedTabLabel(currentSavedCount + 1);

      // Fire both operations concurrently
      var cartRemovePromise = fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: String(variantId), quantity: 0 }),
      });

      onSwymReady(function (swat) {
        swat.addToWishList(
          {
            epi: variantId,
            empi: productId,
            du: window.location.origin + productUrl,
          },
          function () {
            loaded = false;
            try {
              document.documentElement.dispatchEvent(
                new CustomEvent('swym:wishlist-updated', { bubbles: true }),
              );
            } catch (e) { /* ignore */ }
          },
          function () { /* silent — cart remove still proceeds */ },
        );
      });

      cartRemovePromise
        .then(function () {
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:refresh', { bubbles: true }),
          );
        })
        .catch(function () {
          if (cartItemWrapper) cartItemWrapper.style.display = '';
          updateSavedTabLabel(currentSavedCount);
        });
    });
  }

  // ─── Load saved items from Swym ───

  function loadSavedItems(swat) {
    var myGen = ++savedListLoadGen;

    function stillCurrent() {
      return myGen === savedListLoadGen;
    }

    var cart = getCartDrawer();
    var loading = cart
      ? cart.querySelector('.CartDrawer__SavedLoading')
      : document.querySelector('#sidebar-cart .CartDrawer__SavedLoading');
    var empty = cart
      ? cart.querySelector('.CartDrawer__SavedEmpty')
      : document.querySelector('#sidebar-cart .CartDrawer__SavedEmpty');
    var list = cart
      ? cart.querySelector('.CartDrawer__SavedList')
      : document.querySelector('#sidebar-cart .CartDrawer__SavedList');

    // If `form.Cart` was re-rendered (via cart:refresh), DOM is empty — paint cache before fetch.
    if (list && !list.children.length) {
      paintFromCache(list, loading, empty);
    }

    var hadPriorContent = !!(list && list.children.length);

    // Skip redundant network calls when the user just toggles tabs and we have a fresh successful render.
    if (hasFreshCache() && hadPriorContent) {
      return;
    }

    // Only show the spinner on first load. If items are already rendered (DOM or cache),
    // leave them visible while we refetch — error paths must not wipe a working UI.
    if (!hadPriorContent) {
      if (loading) loading.style.display = '';
      if (empty) empty.style.display = 'none';
    }

    // Fail-safe: never leave the spinner up indefinitely if Swym callbacks are dropped.
    setTimeout(function () {
      if (!stillCurrent()) return;
      if (loaded) return;
      showEmptyAfterFailure();
    }, SWYM_CALL_TIMEOUT_MS);

    /** Confirmed empty list from Swym (not a network failure). */
    function showEmptyState() {
      if (!stillCurrent()) return;
      if (loading) loading.style.display = 'none';
      if (list) list.innerHTML = '';
      if (empty) empty.style.display = '';
      updateSavedTabLabel(0);
      savedItemsCache = { items: null, products: null, ts: Date.now() };
      loaded = true;
    }

    /**
     * Network/SDK failure (e.g. Swym relay blocked in incognito → status 0).
     * Preserve any prior rendered content rather than wiping to "empty" — error ≠ empty.
     * If DOM is empty but we have cache, restore from cache as a last resort.
     */
    function showEmptyAfterFailure() {
      if (!stillCurrent()) return;
      if (loading) loading.style.display = 'none';
      loaded = false;
      if (hadPriorContent) return;
      if (paintFromCache(list, loading, empty)) return;
      if (empty) empty.style.display = '';
    }

    function renderSavedFromItems(items) {
      if (!stillCurrent()) return;
      if (!items || items.length === 0) {
        showEmptyState();
        return;
      }

      var recent = items.slice();

      var handles = [];
      var products = {};

      recent.forEach(function (item) {
        var handle = extractHandle(item.du);
        if (handle && handles.indexOf(handle) === -1) {
          handles.push(handle);
        }
      });

      Promise.all(
        handles.map(function (handle) {
          return fetchProduct(handle).then(function (product) {
            if (product) products[product.id] = product;
          });
        }),
      ).then(function () {
        if (!stillCurrent()) return;
        if (loading) loading.style.display = 'none';
        renderItems(list, recent, products);
        savedItemsCache = { items: recent, products: products, ts: Date.now() };
        loaded = true;
      });
    }

    function tryLegacyThenEmpty() {
      if (
        !Core.invokeFetchWishlist(
          swat,
          renderSavedFromItems,
          showEmptyAfterFailure,
        )
      ) {
        showEmptyAfterFailure();
      }
    }

    /**
     * addToWishList from PDP vs “Save for Later” can live on different Swym lids (wl vs _saveForLater).
     * Merge every list’s rows (same ordering as swym-wishlist: non–save-for-later first), dedupe by variant,
     * then renderSavedFromItems takes the last MAX_ITEMS — so cart “Saved” matches /pages/wishlist content.
     */
    function loadMergedFromAllLists(ordered) {
      var merged = [];

      function step(index) {
        if (!stillCurrent()) return;
        if (index >= ordered.length) {
          if (merged.length === 0) {
            tryLegacyThenEmpty();
            return;
          }
          renderSavedFromItems(Core.dedupeWishlistRowsKeepLast(merged));
          return;
        }
        var listMeta = ordered[index];
        if (!listMeta || !listMeta.lid) {
          step(index + 1);
          return;
        }
        swat.fetchListDetails(
          { lid: listMeta.lid },
          function (response) {
            if (!stillCurrent()) return;
            merged = merged.concat(Core.extractListItems(response));
            step(index + 1);
          },
          function () {
            step(index + 1);
          },
        );
      }

      step(0);
    }

    swat.fetchLists({
      callbackFn: function (rawLists) {
        if (!stillCurrent()) return;
        var lists = Core.normalizeFetchListsPayload(rawLists);
        if (!lists.length) {
          tryLegacyThenEmpty();
          return;
        }

        var withLid = lists.filter(function (l) {
          return l && l.lid;
        });
        if (!withLid.length) {
          tryLegacyThenEmpty();
          return;
        }

        withLid.sort(function (a, b) {
          return (
            (Core.isSaveForLaterList(a) ? 1 : 0) -
            (Core.isSaveForLaterList(b) ? 1 : 0)
          );
        });

        loadMergedFromAllLists(withLid);
      },
      errorFn: function () {
        tryLegacyThenEmpty();
      },
    });
  }

  // ─── Render items ───

  function renderItems(container, items, products) {
    if (!container) return;
    container.innerHTML = '';

    items.forEach(function (item) {
      var product = products[item.empi] || null;
      var variant = null;

      if (product && product.variants) {
        variant =
          product.variants.find(function (v) {
            return String(v.id) === String(item.epi);
          }) || null;
      }

      var title = formatTitle(product ? product.title : item.dt || '');
      var price = product
        ? formatMoney(variant ? variant.price : product.price)
        : formatMoney(item.pr ? item.pr * 100 : 0);
      var imgUrl = item.iu || '';
      if (product && product.featured_image) {
        imgUrl = product.featured_image;
      }
      var productUrl = item.du || '#';
      var metaParts = product && variant ? getVariantMeta(product, variant) : [];
      if (!metaParts.length && item.vi) {
        var viParts = item.vi.split(/\s*\/\s*/);
        if (viParts[0]) metaParts.push('Color: ' + viParts[0]);
        if (viParts[1]) metaParts.push('Size: ' + viParts[1]);
      }

      var html = '<div class="CartDrawer__SavedItem">';
      html +=
        '<div class="CartDrawer__SavedItem__ImageWrapper">' +
        '<a class="CartDrawer__SavedItem__ImageLink" href="' + escapeAttr(productUrl) + '">' +
        '<img class="CartDrawer__SavedItem__Image" src="' + escapeAttr(imgUrl) + '" alt="' + escapeAttr(title) + '" loading="lazy">' +
        '</a>' +
        '</div>';
      html += '<div class="CartDrawer__SavedItem__Info">';
      html += '<div class="CartDrawer__SavedItem__InfoWrapper">';
      html +=
        '<h2 class="CartItem__Title Heading"><a class="CartDrawer__SavedItem__TitleLink" href="' +
        escapeAttr(productUrl) +
        '">' +
        escapeHtml(title) +
        '</a></h2>';
      html += '<div class="CartDrawer__SavedItem__PriceWrapper"><span class="CartDrawer__SavedItem__Price Price">' + price + '</span></div>';
      html += '</div>';

      html += '<div class="CartDrawer__SavedItem__MetaWrapper">';
      metaParts.forEach(function (meta) {
        html +=
          '<p class="CartItem__Meta Heading Text--subdued" style="margin: 0">' +
          escapeHtml(meta) +
          '</p>';
      });

      html += '<p class="CartItem__Meta Heading Text--subdued" style="margin: 0">Qty: 1</p>';
      html += '</div>';
      html +=
        '<button type="button" class="CartDrawer__SavedAddToBag" data-epi="' +
        escapeAttr(String(item.epi)) +
        '" data-empi="' +
        escapeAttr(String(item.empi != null ? item.empi : '')) +
        '" data-du="' +
        escapeAttr(item.du || '') +
        '">Add to Bag</button>';
      html += '</div>';
      html += '</div>';

      container.insertAdjacentHTML('beforeend', html);
    });

    updateSavedTabLabel(items.length);

    // Bind add-to-bag buttons
    container.querySelectorAll('.CartDrawer__SavedAddToBag').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var epi = parseInt(btn.getAttribute('data-epi'), 10);
        addToBag(epi, btn);
      });
    });
  }

  function addToBag(variantId, btn) {
    btn.textContent = 'Adding...';
    btn.disabled = true;

    // Optimistic: remove card from saved panel immediately
    var card = btn.closest('.CartDrawer__SavedItem');
    var savedList = document.querySelector('.CartDrawer__SavedList');
    var currentSavedCount = savedList
      ? savedList.querySelectorAll('.CartDrawer__SavedItem').length
      : (savedItemsCache.items ? savedItemsCache.items.length : 0);

    if (card) card.style.display = 'none';
    updateSavedTabLabel(Math.max(0, currentSavedCount - 1));

    var duAttr = btn.getAttribute('data-du') || '';
    var swymPayload = {
      epi: variantId,
      epiStr: String(variantId),
      empi: parseInt(btn.getAttribute('data-empi'), 10),
      du: Core.absoluteProductUrl(duAttr),
    };

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: String(variantId), quantity: 1 }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Failed');
        return res.json();
      })
      .then(function () {
        // Remove from Swym async — don't block the refresh
        onSwymReady(function (swat) {
          loaded = false;
          Core.removeVariantFromSwym(swat, swymPayload, function () {
            try {
              document.documentElement.dispatchEvent(
                new CustomEvent('swym:wishlist-updated', { bubbles: true }),
              );
            } catch (e) { /* ignore */ }
          }, function () { /* silent */ });
        });
        // Refresh cart immediately
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true }),
        );
      })
      .catch(function () {
        // Undo optimistic changes on failure
        if (card) card.style.display = '';
        updateSavedTabLabel(currentSavedCount);
        btn.textContent = 'Add to Bag';
        btn.disabled = false;
      });
  }

  function getSavedTabCount() {
    var tab = document.querySelector('[data-cart-tab="saved"]');
    if (!tab) return 0;
    var m = tab.textContent.match(/\((\d+)\)/);
    return m ? parseInt(m[1], 10) : 0;
  }

  function updateSavedTabLabel(count) {
    var tab = document.querySelector('[data-cart-tab="saved"]');
    if (!tab) return;
    tab.textContent = count > 0 ? 'Saved (' + count + ')' : 'Saved';
  }

  // ─── Cart count sync ───

  var cartCountTimer = null;

  function syncBagTabFromNavCount() {
    var countEl = document.querySelector('.Header__CartCount');
    var bagTab = document.querySelector('[data-cart-tab="bag"]');
    if (!bagTab) return;
    var n = countEl ? (parseInt(countEl.textContent, 10) || 0) : null;
    if (n !== null) {
      bagTab.textContent = n > 0 ? 'Bag (' + n + ')' : 'Bag';
    }
  }

  function updateCartCount() {
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var bagTab = document.querySelector('[data-cart-tab="bag"]');
        if (bagTab) {
          bagTab.textContent = cart.item_count > 0 ? 'Bag (' + cart.item_count + ')' : 'Bag';
        }
      })
      .catch(function () {});
  }

  // Watch .Header__CartCount for mutations — theme.js updates it on every _replaceContent
  // (covers quantity changes and item removals that don't dispatch cart:refresh).
  (function watchNavCartCount() {
    var target = document.querySelector('.Header__CartCount');
    if (!target) return;
    new MutationObserver(function () {
      syncBagTabFromNavCount();
    }).observe(target, { childList: true, characterData: true, subtree: true });
  })();

  // Debounced listener for cart:refresh + invalidate Saved list (Save for Later, line-item changes)
  document.documentElement.addEventListener('cart:refresh', function () {
    resetCartDrawerToBagTab();
    invalidateSavedCacheAndMaybeReload(550);
    clearTimeout(cartCountTimer);
    cartCountTimer = setTimeout(updateCartCount, 500);
  });

  // product:added fires when ATC succeeds; _rerenderCart runs but no cart:refresh is dispatched.
  document.addEventListener('product:added', function () {
    clearTimeout(cartCountTimer);
    cartCountTimer = setTimeout(updateCartCount, 600);
  });

  document.documentElement.addEventListener('swym:wishlist-updated', function () {
    invalidateSavedCacheAndMaybeReload(450);
  });

  // Pre-populate the Saved tab count from the wishlist-btn page-load fetch,
  // so the number is visible before the user ever clicks the Saved tab.
  document.documentElement.addEventListener('swym:wishlist-count', function (e) {
    if (loaded) return; // saved tab already rendered — trust that count
    var count = e.detail && typeof e.detail.count === 'number' ? e.detail.count : 0;
    updateSavedTabLabel(count);
  });

  // ─── Boot ───

  function boot() {
    initTabs();
    initSaveForLater();
    watchCartDrawerCloseForTabReset();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
